import { useState } from "react";
import {
  ORDER_KEY,
  REPEATABLE,
  defaultEntries,
  instanceKey,
  move,
  orderedEntries,
  writeOrder,
  type Entry,
} from "../funnel/order";
import { FUNNEL, REPEATABLE_STEP, stepShowKey } from "../routes/registry";
import { defaultOnOf } from "../settings/catalog";
import { Provenance } from "./Provenance";
import { useSettings } from "../settings/context";
import { Icon } from "../ui/Icon";

/**
 * ═══ LE PARCOURS : SON ORDRE, ET COMBIEN D'ÉTAPES ═══
 *
 * Une ligne par étape servie, dans l'ordre où le participant les traverse. Deux flèches
 * pour déplacer, et — pour l'étape répétable — de quoi en ajouter une et la retirer.
 *
 * ⚠️ UN REFUS SE DIT. Certaines étapes ne se déplacent pas n'importe où : la billetterie
 * se vend jour par jour, les conférences se filtrent sur la société, la confirmation est
 * le seul écran qui enregistre. Une flèche qui ne fait rien sans un mot se lit comme une
 * panne — le motif s'affiche sous la liste, et il vient de la règle elle-même
 * (`funnel/order.ts`), jamais d'un texte recopié ici.
 *
 * ⚠️ L'ORDRE NE SE CASCADE PAS : il s'écrit au niveau commun de l'événement, comme
 * l'ordre des pages du site. Un exposant qui traverserait les étapes dans un autre ordre
 * qu'un visiteur rendrait le fil d'Ariane impossible à raisonner.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function StepsManager() {
  const { value, write, detail, saving, scope } = useSettings();
  const [refusal, setRefusal] = useState<string | null>(null);

  const entries = orderedEntries(value(ORDER_KEY));

  const save = (next: ReadonlyArray<Entry>) => {
    setRefusal(null);
    write(ORDER_KEY, writeOrder(next));
  };

  const movePage = (index: number, direction: -1 | 1) => {
    const moved = move(entries, index, direction);
    if (moved.entries) return save(moved.entries);
    setRefusal(moved.refusal);
  };

  /**
   * ⚠️ UN IDENTIFIANT QUI NE PEUT PAS COLLISIONNER AVEC UN EXISTANT, et qui ne dépend ni
   * de l'heure ni du hasard — les deux sont interdits dans ce dépôt (ils cassent la
   * reprise d'un enregistrement). On prend le plus petit rang libre.
   */
  const add = () => {
    if (!REPEATABLE_STEP) return;
    const taken = new Set(
      entries.filter((e) => e.key === REPEATABLE).map((e) => e.instance ?? "1"),
    );
    let n = 2;
    while (taken.has(String(n))) n += 1;
    const fresh: Entry = { key: REPEATABLE, instance: String(n) };

    /* Elle se pose JUSTE APRÈS la dernière du même nom : c'est là que l'organisateur
       l'attend, et c'est un emplacement qui respecte les dépendances par construction. */
    const last = entries.map((e) => e.key).lastIndexOf(REPEATABLE);
    const next = [...entries];
    next.splice(last + 1, 0, fresh);
    save(next);
  };

  const remove = (index: number) => {
    const next = entries.filter((_, i) => i !== index);
    save(next);
  };

  const stepDe = (entry: Entry) => FUNNEL.find((s) => s.key === entry.key);

  /** Ce que le fil affiche aujourd'hui — le mot écrit, sinon celui du registre. */
  const name = (entry: Entry) => {
    const step = stepDe(entry);
    if (!step) return entry.key;
    const written = value(instanceKey(step.funnel, entry.instance));
    return typeof written === "string" && written.trim() ? written : step.stepName;
  };

  return (
    <div className="stm">
      <ol className="stm__l">
        {entries.map((entry, i) => {
          /* Une instance AJOUTÉE se retire d'ici ; la première, elle, se retire par son
             interrupteur, comme les autres étapes — sinon on perdrait tout ce qu'elle
             porte de réglages en un clic. */
          const removable =
            entry.key === REPEATABLE && (entry.instance ?? "1") !== "1";

          const step = stepDe(entry);
          if (!step) return null;

          const nameKey = instanceKey(step.funnel, entry.instance);
          const servedKey = stepShowKey(step, entry.instance);
          /* L'état EFFECTIF, clé absente comprise : le catalogue dit qu'une étape est
             servie tant qu'on ne l'éteint pas — sauf celles qu'on ajoute (`optIn`). */
          const raw = value(servedKey);
          const served =
            typeof raw === "boolean" ? raw : defaultOnOf(stepShowKey(step));

          return (
            <li className="stm__r" key={`${entry.key}:${entry.instance ?? ""}`}>
              <span className="stm__n" aria-hidden="true">
                {i + 1}
              </span>

              {/* ⚠️ LE NOM SE RÈGLE ICI, sur la ligne de l'étape — et non trois lignes
                  plus bas dans une famille qui répétait chaque étape. Le repli reste
                  celui du registre : un champ vide n'efface pas le mot du fil, il rend
                  la main au produit, et le voir en gris le dit. */}
              <input
                className="stm__i"
                type="text"
                value={typeof value(nameKey) === "string" ? (value(nameKey) as string) : ""}
                placeholder={step.stepName}
                aria-label={`Nom de l'étape ${step.stepName}`}
                onChange={(e) => write(nameKey, e.target.value)}
              />

              <span className="stm__a">
                {/* ⚠️ LA CONFIRMATION N'A PAS D'INTERRUPTEUR : c'est le seul écran qui
                    ENREGISTRE. Un bouton grisé aurait promis un réglage inexistant. */}
                {step.removable ? (
                  <button
                    type="button"
                    className="sw"
                    aria-pressed={served}
                    aria-label={`Servir ${name(entry)}`}
                    onClick={() => write(servedKey, !served)}
                  />
                ) : (
                  <span className="stm__f" title="Toujours servie" aria-hidden="true">
                    <Icon name="verrou" />
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Monter ${name(entry)}`}
                  onClick={() => movePage(i, -1)}
                >
                  <Icon name="chevron" />
                </button>
                <button
                  type="button"
                  className="stm__d"
                  aria-label={`Descendre ${name(entry)}`}
                  onClick={() => movePage(i, 1)}
                >
                  <Icon name="chevron" />
                </button>
                {removable && (
                  <button
                    type="button"
                    className="stm__x"
                    aria-label={`Retirer ${name(entry)}`}
                    onClick={() => remove(i)}
                  >
                    <Icon name="croix" />
                  </button>
                )}
              </span>

              {/* ⚠️ LA PROVENANCE SEULEMENT QUAND IL Y A QUELQUE CHOSE À DIRE. Chaque
                  ligne du panneau porte la sienne ; dix pastilles « non définie » dans
                  une liste de dix étapes ne diraient rien et la rendraient illisible.
                  Dès qu'un mot est écrit — ici, dans une autre langue, pour une typologie
                  — elle réapparaît, avec son « reprendre l'héritée » et sa traduction. */}
              {(() => {
                const d = detail(nameKey);
                /* ⚠️ MÊME PRÉDICAT QUE `Row` : traduire n'est offert que dans la langue de
                   RÉFÉRENCE, sur une valeur écrite non vide, et sous une typologie seulement
                   si quelque chose y est écrit — sinon le clic figerait la valeur héritée en
                   surcharge de typologie dans toutes les langues. */
                const translatable =
                  saving &&
                  scope.languageId === scope.defaultLanguageId &&
                  typeof d.value === "string" &&
                  d.value.trim().length > 0 &&
                  (!scope.typoId || d.isSet);
                /* ⚠️ « ÉCRIT ICI » ET « A QUELQUE CHOSE À DIRE » NE SONT PAS LA MÊME
                   CHOSE. Une clé peut exister en valant "" : le fil reprend alors le mot
                   du produit, et annoncer « commune » sous un champ gris ne décrirait
                   rien. Mais un "" écrit à CE niveau masque le mot du niveau du dessous
                   — c'est justement là que « reprendre l'héritée » doit rester joignable,
                   d'où les deux conditions plutôt qu'une. */
                const aDire =
                  d.isSet || (typeof d.value === "string" && d.value.trim() !== "");
                return aDire ? (
                  <span className="stm__p">
                    <Provenance
                      settingKey={nameKey}
                      translatable={translatable}
                      prose
                    />
                  </span>
                ) : null;
              })()}
            </li>
          );
        })}
      </ol>

      {/* Le motif du dernier refus. Il disparaît au premier déplacement qui aboutit :
          une explication qui reste après avoir été comprise se lit comme un reproche. */}
      {refusal && (
        <p className="stm__no" role="status">
          {refusal}
        </p>
      )}

      <div className="stm__b">
        {REPEATABLE_STEP && (
          <button type="button" onClick={add}>
            Ajouter une étape « {REPEATABLE_STEP.stepName} »
          </button>
        )}
        {writeOrder(entries) !== writeOrder(defaultEntries()) && (
          <button type="button" onClick={() => save(defaultEntries())}>
            Remettre l'ordre d'origine
          </button>
        )}
      </div>

      {/* ⚠️ DIT UNE FOIS, ET PAS À CHAQUE REFUS : ce qu'on retire ici n'efface rien.
          Les réponses déjà données restent en base sous leur modèle — on remet l'étape,
          tout est là. Sans cette phrase, « Retirer » se lit comme une suppression. */}
      <p className="stm__h">
        Retirer une étape ne supprime aucune réponse : elles restent enregistrées, et
        reviennent avec elle.
      </p>
    </div>
  );
}
